<template>
    <Teleport to="body">
        <Transition name="ask-box">
            <div
                v-if="isOpen"
                ref="panel"
                class="ask-box"
                data-testid="ask-box"
                role="dialog"
                aria-label="Ask about this stek"
            >
                <!-- A hint, not a control. Clicking away closes the box, which
                     is the same gesture the search dialog's dismissable mask
                     already trains; this is only here to say that Escape does
                     it too, without taking a seat in the input row. -->
                <span class="ask-box__hint" data-testid="ask-box-hint"
                    >esc</span
                >

                <!-- Everything that is not the typing line sits above it, and
                     the typing line never moves. -->
                <div
                    v-if="hasConversation"
                    ref="conversation"
                    class="ask-box__conversation"
                >
                    <!-- Everything already answered in this sitting, oldest
                         first. Above the live exchange so the answer being
                         given to a clarifying question can be read against the
                         question that asked for it. -->
                    <AskExchange
                        v-for="(exchange, index) in history"
                        :key="index"
                        :question="exchange.question"
                        :answer="exchange.answer"
                        @navigate="dismiss"
                        @handed-off="dismiss"
                    />

                    <p
                        v-if="askedQuestion"
                        class="ask-box__question"
                        data-testid="ask-box-question"
                    >
                        {{ askedQuestion }}
                    </p>

                    <hr v-if="askedQuestion" class="ask-box__rule" />

                    <div
                        v-if="isAsking"
                        class="ask-box__thinking"
                        data-testid="ask-box-thinking"
                    >
                        <i class="pi pi-spin pi-spinner" />
                        <!-- The last step it reported, or "Thinking…" until
                             there is one. The latest rather than all of them:
                             what a reader wants from this line is evidence that
                             something is still happening, and a growing list
                             would push the typing line around to say it. -->
                        <span
                            class="ask-box__step"
                            data-testid="ask-box-step"
                            >{{ currentStep }}</span
                        >
                        <span class="ask-box__elapsed">{{ elapsed }}</span>
                    </div>

                    <!-- eslint-disable-next-line vue/no-v-html -->
                    <div
                        v-else-if="answer"
                        class="ask-box__answer"
                        data-testid="ask-box-answer"
                        v-html="renderedAnswer"
                    />

                    <p
                        v-else-if="error"
                        class="ask-box__error"
                        data-testid="ask-box-error"
                    >
                        {{ error }}
                    </p>

                    <!-- Below the answer, and only while there is one: both are
                         about it, so during a run the previous answer's
                         leftovers would be describing something already gone.
                         Kept outside the v-if/v-else-if chain above, which is
                         a three-way choice between thinking, answer and error. -->
                    <AskReferences
                        v-if="showsAnswer && references.length"
                        :references="references"
                        @navigate="dismiss"
                    />

                    <AskThreadOffer
                        v-if="showsAnswer && offerThread"
                        :offer="offerThread"
                        :question="askedQuestion"
                        @handed-off="dismiss"
                    />
                </div>

                <div class="ask-box__input-row">
                    <InputText
                        ref="questionInput"
                        v-model="draft"
                        class="ask-box__input"
                        data-testid="ask-box-input"
                        :placeholder="placeholder"
                        :disabled="isAsking"
                        autocomplete="off"
                        @keydown.enter="submit"
                    />
                    <Button
                        v-if="draft.trim() && !isAsking"
                        class="ask-box__send"
                        icon="pi pi-send"
                        aria-label="Ask"
                        data-testid="ask-box-send"
                        rounded
                        @click="submit"
                    />
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import {
    computed,
    nextTick,
    onUnmounted,
    ref,
    useTemplateRef,
    watch,
} from "vue";

import AskExchange from "@/components/AskBox/AskExchange.vue";
import AskReferences from "@/components/AskBox/AskReferences.vue";
import AskThreadOffer from "@/components/AskBox/AskThreadOffer.vue";
import { renderAnswer } from "@/components/AskBox/ask-content";
import { useStekManager } from "@/composables/stek-manager";
import { useAskManager, useAskStore } from "@/stores/ask-store";

const store = useAskStore();
const {
    isOpen,
    askedQuestion,
    answer,
    references,
    offerThread,
    history,
    isAsking,
    error,
    progressSteps,
    startedAtMs,
} = useAskManager();
const { stek } = useStekManager();

const draft = ref("");
const questionInput = useTemplateRef<{ $el: HTMLElement }>("questionInput");
const panel = useTemplateRef<HTMLElement>("panel");
const conversation = useTemplateRef<HTMLElement>("conversation");

const placeholder = computed(() =>
    stek.value?.name ? `Ask about ${stek.value.name}…` : "Ask about this stek…"
);

const hasConversation = computed(() =>
    Boolean(
        askedQuestion.value ||
        isAsking.value ||
        error.value ||
        answer.value ||
        history.value.length
    )
);

const currentStep = computed(
    () => progressSteps.value[progressSteps.value.length - 1] ?? "Thinking…"
);

/** An answer is on screen — the one state where its trimmings belong. */
const showsAnswer = computed(() => !isAsking.value && Boolean(answer.value));

const renderedAnswer = computed(() => renderAnswer(answer.value || ""));

// The conversation scrolls once it is taller than the panel allows, and what
// matters is always its bottom: the live exchange, just above the typing line.
// Without this a follow-up would push the thing being answered out of sight at
// the exact moment it was asked.
const scrollToLive = () => {
    void nextTick(() => {
        const element = conversation.value;
        if (element) {
            element.scrollTop = element.scrollHeight;
        }
    });
};

watch([askedQuestion, answer, () => history.value.length], scrollToLive);

// How long it has actually been, beside what it is actually doing. Neither is
// an indeterminate bar, and between them the box never has to claim progress it
// cannot see.
//
// Counted from the server's `started_at_ms` rather than from the moment this tab
// noticed. The run outlives the box now: reopening mid-run has to show a clock
// that says how long the *run* has been going, not how long this browser has
// been watching it.
const secondsElapsed = ref(0);
let ticker: ReturnType<typeof setInterval> | undefined;

const stopTicker = () => {
    if (ticker) {
        clearInterval(ticker);
        ticker = undefined;
    }
};

const tick = () => {
    const startedAt = startedAtMs.value;
    secondsElapsed.value = startedAt
        ? Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
        : 0;
};

watch(isAsking, (asking) => {
    stopTicker();
    secondsElapsed.value = 0;
    if (asking) {
        tick();
        ticker = setInterval(tick, 1000);
        return;
    }
    // Take the focus back when the run ends. `:disabled` on the input blurs it
    // while the answer is being fetched — standard browser behaviour for a
    // focused element that becomes disabled — and without this the box is
    // sitting open inviting a follow-up that needs a mouse click first.
    void nextTick(() => {
        if (isOpen.value) {
            questionInput.value?.$el?.focus?.();
        }
    });
});

const elapsed = computed(() => {
    const minutes = Math.floor(secondsElapsed.value / 60);
    const seconds = secondsElapsed.value % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
});

const submit = () => {
    const question = draft.value;
    if (!question.trim() || isAsking.value) {
        return;
    }
    // Clear immediately: the typing line is always empty and always in the same
    // place, and the question reappears above it as what was asked.
    draft.value = "";
    void store.ask(question);
};

const dismiss = () => {
    draft.value = "";
    store.close();
};

// Escape is a raw listener rather than a registered hotkey on purpose: the
// hotkey layer ignores un-modified keys while focus is inside an input
// (`composables/hotkeys/index.ts`), and the box's input is focused the whole
// time it is open — so a registered Escape would never fire here.
const onKeydown = (event: KeyboardEvent) => {
    if (event.key === "Escape" && isOpen.value) {
        event.preventDefault();
        dismiss();
    }
};

// Clicking away closes it — the same gesture PrimeVue's `dismissableMask` gives
// the search dialog, so it is already the learned way out of an overlay here.
// It is also the reason the box needs no close button: a control for this would
// be a second way to do what clicking anywhere already does.
//
// `mousedown` rather than `click` so a drag that starts inside the box and ends
// outside it — selecting the answer text, which people do — does not dismiss.
const onPointerDown = (event: MouseEvent) => {
    if (!isOpen.value) {
        return;
    }
    const target = event.target as Node | null;
    if (target && panel.value?.contains(target)) {
        return;
    }
    dismiss();
};
window.addEventListener("keydown", onKeydown);
window.addEventListener("mousedown", onPointerDown);

onUnmounted(() => {
    window.removeEventListener("keydown", onKeydown);
    window.removeEventListener("mousedown", onPointerDown);
    stopTicker();
});

watch(isOpen, (open) => {
    if (!open) {
        draft.value = "";
        stopTicker();
        return;
    }
    void nextTick(() => {
        questionInput.value?.$el?.focus?.();
    });
});
</script>

<style scoped>
.ask-box {
    position: fixed;
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    width: min(720px, calc(100vw - 3rem));
    /* Above the menubar (1000), hints (1001) and a maximized panel (1200): the
       box is a global surface and is dismissed in one keystroke. */
    z-index: 1250;
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    padding: 1rem 1.25rem 1.25rem;
    background: var(--p-overlay-popover-background);
    border: 1px solid var(--p-overlay-popover-border-color);
    border-radius: var(--p-overlay-popover-border-radius);
    /* Deliberately heavier than the popover token, which is tuned for something
       anchored to the element that opened it. This is anchored to nothing and
       floats over the whole app, and at the token's weight it read as stuck to
       the bottom of the page. Same family as MaximizablePanel's, scaled down
       for a smaller surface.

       No mode colour, and that is the point: every other surface here is tinted
       by the mode you are standing in, and this one deliberately is not. The
       box answers across Design, Build and Run at once, so wearing one mode's
       colour would claim a scope it does not have. */
    box-shadow:
        0 0.75rem 2rem rgb(0 0 0 / 18%),
        0 0.125rem 0.375rem rgb(0 0 0 / 8%);
    color: var(--text-color);
}

.ask-box__conversation {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 0.25rem;
    max-height: min(50vh, 420px);
    overflow-y: auto;
}

/* The question is context, not content: it is there so you can see what was
   asked, and it must not compete with the answer. Before this the two were
   near-identical grey and you could not tell them apart at a glance. */
.ask-box__question {
    margin: 0;
    color: var(--p-text-muted-color);
    font-size: 0.85rem;
}

.ask-box__rule {
    margin: 0;
    border: 0;
    border-top: 1px solid var(--p-surface-200);
}

.ask-box__thinking {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    color: var(--p-text-muted-color);
    font-size: 0.9rem;
}

/* Takes the slack so the clock stays pinned right and does not jitter as the
   step text changes length. */
.ask-box__step {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.ask-box__elapsed {
    flex: 0 0 auto;
    font-variant-numeric: tabular-nums;
}

.ask-box__error {
    margin: 0;
    color: var(--p-red-500, #e24c4c);
    font-size: 0.9rem;
}

.ask-box__answer {
    font-size: 0.95rem;
    line-height: var(--body-line-height, 1.5);
}

.ask-box__answer :deep(p) {
    margin: 0.5em 0;
}

.ask-box__answer :deep(p:first-child) {
    margin-top: 0;
}

.ask-box__answer :deep(p:last-child) {
    margin-bottom: 0;
}

.ask-box__answer :deep(a) {
    color: var(--p-primary-color);
    text-decoration: underline;
    overflow-wrap: anywhere;
}

.ask-box__answer :deep(code) {
    background: var(--p-surface-200);
    padding: 0.1em 0.3em;
    font-size: 0.85em;
}

.ask-box__answer :deep(ul),
.ask-box__answer :deep(ol) {
    margin: 0.5em 0;
    padding-left: 1.4em;
    list-style-position: outside;
}

.ask-box__input-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.ask-box__input {
    flex: 1 1 auto;
}

/* Deliberately not a chip and not a button: a bordered control here competed
   with the send button and implied it was the way out, when clicking away
   already is. Just a quiet word in the corner.

   In the flex flow rather than absolutely positioned into the panel's padding,
   which is what made it collide with the input below and with a question long
   enough to wrap. It gets its own line and cannot touch anything. */
.ask-box__hint {
    align-self: flex-end;
    color: var(--p-text-muted-color);
    font-size: 0.7rem;
    line-height: 1;
    letter-spacing: 0.02em;
    opacity: 0.6;
    pointer-events: none;
    user-select: none;
}

.ask-box-enter-active,
.ask-box-leave-active {
    transition:
        opacity 160ms ease,
        transform 160ms ease;
}

.ask-box-enter-from,
.ask-box-leave-to {
    opacity: 0;
    transform: translate(-50%, 12px);
}

.ask-box-enter-to,
.ask-box-leave-from {
    transform: translate(-50%, 0);
}
</style>
